import { PageLayout } from '@kizi/comparator-core';
import { getTabs } from '../../../utils/overview';
import {
  SORTING_DESCENDING_ORDER_TRIPLE_PLAY,
  SORTING_OPTIONS_TRIPLE_PLAY,
  SORTING_REFERENCE_TRIPLE_PLAY,
} from '../../../utils/sorting';
import { combinationDiscount } from '../content/decisionAidTabs';
import {
  basicMembershipBenefitsItems,
  howWeCompareItems,
  privacyStatementItems,
} from '../content/modalsContent';
import triplePlayProvvalueerOptions from '../content/triplePlayProviderOptions';
import customerHelpContent from '../widgets/helpContent/customerHelpContent';

const personalData = {
  type: 'PersonalData2',
  props: {
    header: {
      type: 'Heading',
      props: {
        mt: 0,
        variant: 'H4',
        as: 'h4',
        children: 'Je gegevens',
      },
    },
    mb: 6,
    variant: 'primaryCard',
    fieldNames: [
      'startWidget.fullAddress',
      'startWidget.postcode',
      'startWidget.housenumber',
      'startWidget.housenumberaddition',
      'startWidget.street',
      'startWidget.city',
      'startWidget.productTypes',
      'startWidget.isMove',
      'startWidget.currentProvider',
      'startWidget.optionalemail',
    ],
    viewModeLayout: [
      'fullAddress',
      {
        type: 'LabelWithFieldname',
        props: {
          label: 'Huidige provider:',
          mt: 3,
          options: 'startWidget.providerOptions',
          selected: 'startWidget.currentProvider',
        },
      },
    ],
    formLayout: [
      {
        type: 'AddressWithContext',
        props: {
          autoForward: true,
          inputProps: {
            postcode: {
              _label: 'Postcode',
              placeholder: '1234AB',
            },
            houseNumber: {
              _label: 'Huisnr.',
              placeholder: '123',
              type: 'tel',
            },
            addition: {
              _label: 'Toev.',
              placeholder: 'A',
            },
          },
          fieldNames: {
            addition: 'housenumberaddition',
            houseNumber: 'housenumber',
            postcode: 'postcode',
            city: 'city',
            street: 'street',
            fullAddress: 'fullAddress',
          },
        },
      },
      {
        type: 'FormInput',
        props: {
          name: 'isMove',
          component: {
            type: 'Inputs.TileRadioGroup',
            props: {
              _containerProps: { my: 3 },
              _label: 'Gaat het om een verhuizing?',
              direction: 'horizontal',
              displayTilesInRow: true,
              style: {
                height: '54px',
                borderRadius: '4px',
              },
              radios: [
                { text: 'Ja', value: 'true' },
                { text: 'Nee', value: 'false' },
              ],
            },
          },
        },
      },
      {
        type: 'FormInput',
        props: {
          name: 'currentProvider',
          component: {
            type: 'Inputs.Select',
            props: {
              _fieldName: 'startWidget.currentProvider',
              options: triplePlayProvvalueerOptions,
              optionsFieldName: 'startWidget.providerOptions',
            },
          },
        },
      },
    ],
    viewModeFooter: [
      {
        type: 'SliderControl',
        props: {
          goto: 1,
          variant: 'transparent',
          mt: 3,
          fontSize: [2, 3],
          children: 'Wijzig',
        },
      },
    ],
  },
};

const overviewLayout: PageLayout = {
  name: 'overview',
  components: [
    {
      type: 'OverviewPageNew',
      props: {
        paramsFromFields: [
          'startWidget.postcode',
          'startWidget.housenumber',
          'startWidget.housenumberaddition',
          'startWidget.currentProvider',
          'overview.filterGraphQL',
        ],
        filtersToIgnore: ['productTypes'],
        filters: [
          {
            type: 'component',
            id: 'productTypes',
            sectionHeader: 'Algemeen',
            componentProps: {
              type: 'ContentTypeCheckbox',
              props: {
                _fieldName: 'startWidget.productTypes',
                // partOfFilter: true,
                label: 'Pakket',
                labelProps: {
                  fontSize: 3,
                },
              },
            },
          },
          {
            type: 'slider',
            id: 'monthlyPrice',
            key: 'priceInfo.total',
            title: 'Prijs',
          },

          {
            id: 'suppliers',
            isToggleVisible: true,
            key: 'provider.name',
            title: 'Aanbieders',
            type: 'checkbox',
            fields: [],
          },
          {
            id: 'deliveryTime',
            type: 'checkbox',
            fields: [
              {
                label: 'Levertijd binnen 14 dagen',
                value: 14,
                key: 'contractInformation.deliveryTime.text',
              },
            ],
            title: 'Leveringsopties',
          },
          {
            id: 'onceOffCosts',
            type: 'checkbox',
            fields: [
              {
                label: 'Geen aansluitkosten',
                value: 0,
                key: 'onceOffCosts.connectionCosts',
              },
            ],
          },
          {
            type: 'component',
            componentProps: {
              type: 'CombinationDiscount',
              props: {
                buttonLabel: 'Toon combinatiekorting',
                title: 'Combinatiekorting',
                withTabs: false,
                tabs: [combinationDiscount],
                wizardOptions: {
                  startIndex: 0,
                },
                wizardStepsFieldName: 'startWidget.modalSteps',
                filtersFieldName: 'overview.filters',
                fieldName: 'startWidget.decisionAid',
              },
            },
          },

          {
            type: 'slider',
            id: 'downloadSpeed',
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'internet',
            },
            leftValue: 50,
            max: 8000,
            customValues: [50, 100, 400, 1000, 8000],
            valueProps: {
              fontSize: 3,
              textAlign: 'center',
              width: '250px',
            },
            sectionHeader: 'Internet',
            unit: 'Mb/s',
            unitPosition: 'right',
            key: 'servicesWrapper.networkInformation.internetSpeed.downloadSpeedInMb',
            title: 'Downloadsnelheid',
            tooltip:
              'Welke snelheid je nodig hebt hangt af van het gebruik. Om te videobellen heb je aan maximaal 3 Mbit/s voldoende. Streamen van 4K-video kost ongeveer 25 Mbit/s. Maak je op meerdere apparaten tegelijkertijd gebruik van internet dan heb je meer bandbreedte nodig.',
          },
          {
            id: 'signal',
            key: 'servicesWrapper.networkInformation.network.signal',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'internet',
            },
            fields: [
              { label: 'DSL', value: 'dsl' },
              { label: 'Kabel', value: 'kabel' },
              { label: 'Glasvezel', value: 'glasvezel' },
            ],
            title: 'Type verbinding',
            type: 'checkbox',
            tooltip:
              'Glasvezel en kabel zijn aan te raden als je op zoek bent naar een snelle internetverbinding. Bij kabel is een tv-abonnement vaak verplicht. DSL is vaak goedkoper dan glasvezel en kabel. Bij kabel en DSL is de uploadsnelheid vaak een stuk lager dan de downloadsnelheid. Bij glasvezel is de download- en uploadsnelheid gelijk. Dat is handig als je vaak grote bestanden zoals video’s verstuurt.',
          },
          {
            id: 'extraOptions',
            sectionHeader: 'Televisie',
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'television',
            },
            fields: [
              {
                label: 'Opnemen',
                value: 'recordPrograms',
                key: 'servicesWrapper.televisionInfo.recordPrograms.available',
              },
              {
                label: 'Live pauzeren',
                value: 'pauseLiveTv',
                key: 'servicesWrapper.televisionInfo.pauseLiveTv.available',
              },
              {
                label: 'Begin gemist',
                value: 'missedPrograms',
                key: 'servicesWrapper.televisionInfo.missedPrograms.available',
              },
              {
                label: 'Video on demand',
                value: 'videoOnDemand',
                key: 'servicesWrapper.televisionInfo.videoOnDemand.available',
              },
            ],
            isToggleVisible: true,
            shortCheckboxesGroupAmount: 2,
            title: 'Extra opties',
            type: 'checkbox',
          },

          {
            id: 'channelsPackages',
            key: 'servicesWrapper.televisionInfo.channels.channelsPackages.channelPackages',
            title: 'Extra zenderpakketten',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'television',
            },
            fields: [
              { label: 'Ziggo Sport Totaal', value: 'Ziggo Sport Totaal' },
              { label: 'ESPN Compleet', value: 'ESPN Compleet' },
              { label: 'Film1', value: 'Film1' },
            ],
            isToggleVisible: true,
            shortCheckboxesGroupAmount: 2,
            type: 'checkbox',
          },
          {
            type: 'component',
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'television',
            },
            title: 'Specifieke zender',
            id: 'specificTvChannel',
            key: 'channels',
            componentProps: {
              type: 'SpecificChannelSearch',
              id: 'specificTvChannel',
              props: {
                _fieldName: 'overview.filters',
                id: 'specificTvChannel',
                queriesFieldName: 'overview.filterGraphQL',
                _category: 'television',
                optionsFieldName: 'overview.specificTvChannelOptions',
                defaultFiltersFieldName: 'overview.defaultFilters',
                _label: 'Specifieke zender',
                _tooltip:
                  'Geef met de zoekfunctie aan wat je favoriete zenders zijn. Let op dat bepaalde zenders optioneel tegen meerprijs verkrijgbaar zijn.',
              },
            },
          },
          {
            id: 'bellen',
            type: 'checkbox',
            key: 'servicesWrapper.callInfo.numberPorting.available',
            preserveFields: true,
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'call',
            },
            fields: [
              {
                label: 'Gratis nummerbehoud',
                value: true,
                optionTooltip:
                  'Kies alleen pakketten waarbij het mogelijk is om je huidige telefoonnummer te behouden.',
                key: 'servicesWrapper.callInfo.numberPorting.available',
              },
            ],
            title: 'Bellen',
          },
          {
            id: 'installation',
            type: 'checkbox',
            key: 'priceInfo.installation',
            preserveFields: true,
            fields: [
              {
                label: 'Monteur',
                value: true,
                optionTooltip:
                  'Kies alleen pakketten waarbij je een monteur in kan schakelen.',
              },
            ],
            title: 'Installatie en levering',
          },
        ],
        bg: 'second.2',
        navPosition: 'left',
        productsRequest: {},
        selectedSorting: 0,
        selectedFilters: {
          showPriceMode: ['month'],
          monthlyPrice: [0, 100],
          suppliers: [],
          downloadSpeed: [50, 8000],
          signal: ['dsl', 'kabel', 'glasvezel'],
          extraOptions: [],
          onceOffCosts: [],
          deliveryTime: [],
          channelsPackages: [],
          specificTvChannel: [],
          bellen: [],
          installation: [],
        },
        sortingOptions: [
          {
            label: 'Laagste jaarprijs',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Year_Price,
            icon: 'money',
          },
          {
            label: 'Laagste maandprijs',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Month_Price,
            icon: 'money',
          },
          {
            label: 'Hoogste testscore',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Testscore,
            icon: 'money',
          },
          {
            label: 'Meeste TV-zenders',
            value: SORTING_OPTIONS_TRIPLE_PLAY.TV_Channels,
            icon: 'money',
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'television',
            },
          },
          {
            label: 'Hoogste verwachte snelheid',
            value: SORTING_OPTIONS_TRIPLE_PLAY.Speed,
            icon: 'money',
            dependsOn: {
              dependenceProperty: 'productTypes',
              dependenceValue: 'internet',
            },
          },
        ],
        sortingReference: Object.values(SORTING_REFERENCE_TRIPLE_PLAY),
        sortingDescendingOptions: Object.values(
          SORTING_DESCENDING_ORDER_TRIPLE_PLAY,
        ),
        defaultQueryParams: { type: 'tripleplay' },
        header: {
          type: 'PageHeader',
          props: {
            steps: [
              { title: 'Vergelijken' },
              { title: 'Aanvragen', disabled: true },
            ],
            helpContent: customerHelpContent,
            activeStep: 0,
          },
        },
        sideNav: [
          personalData,
          {
            type: 'FiltersWrapper',
            props: {
              fields: [
                'overview.filters',
                'overview.defaultFilters',
                'overview.filterGraphQL',
              ],
            },
          },
          {
            type: 'Usp',
            props: {
              display: ['none', null, 'block'],
              marginTop: 6,
              title: 'Onze experts hebben het beste met je voor:',
              items: [
                '100% onafhankelijk en betrouwbaar advies',
                'Kies uit een groot aanbod',
                'Sluit direct af',
                'Krijg snel een duidelijk overzicht',
                'Consumenten geven ons een 8,4',
              ],
              iconVerticalAlignment: 'start',
            },
          },
        ],
        subHeader: [
          {
            type: 'PersonalDataMinimized',
            props: {
              ...personalData.props,
              header: undefined,
              variant: undefined,
              title: 'Je gegevens',
              containerProps: {
                display: ['block', 'block', 'block', 'none'],
                mt: 4,
              },
            },
          },
          {
            type: 'Div',
            props: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            },
            _children: [
              {
                type: 'Heading',
                props: {
                  variant: 'H2',
                  as: 'h2',
                  children: 'Vergelijken',
                },
              },
              {
                type: 'Div',
                props: {
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
                _children: [
                  {
                    type: 'Div',
                    props: {
                      display: ['none', null, 'initial'],
                    },
                    _children: [
                      {
                        type: 'ModalWithAccordion',
                        props: {
                          buttonProps: { mr: 6 },
                          buttonLabel: 'Hoe vergelijken wij?',
                          buttonIcon: 'checked-doc',
                          items: howWeCompareItems,
                          title: 'Hoe vergelijken wij?',
                        },
                      },
                    ],
                  },
                  {
                    type: 'Div',
                    props: {
                      display: ['initial', null, 'none'],
                    },
                    _children: [
                      {
                        type: 'MailMeThisOverview',
                        props: {
                          title: 'Op een later moment verder gaan?',
                          buttonLabel: 'Bewaar',
                          buttonIcon: 'send',
                          requestEndpoint: {
                            url: `${process.env.KIZI_PUBLIC_API_BASE_URL}/comparisons/:id/sendtoemail/`,
                            options: {
                              method: 'POST',
                              headers: {
                                'Content-type':
                                  'application/x-www-form-urlencoded',
                                Authorization: `Basic ${process.env.KIZI_PUBLIC_API_AUTH_TOKEN}`,
                              },
                            },
                          },
                          requestLogIdName: 'overview.requestLogId',
                          innerModal: {
                            inputLabel: 'E-mailadres',
                            submitButton: 'Verzenden',
                            text: 'Vul je e-mailadres in en wij sturen je dit overzicht. Zo kun je het later nog eens rustig bekijken voordat je een beslissing neemt.',
                            textBeforePrivacyModal:
                              'We gebruiken je e-mailadres voor het toesturen van dit overzicht en we zullen je nog 1 herinnering sturen. Lees onze',
                            textAfterPrivacyModal: 'voor meer informatie',
                          },
                          privacyModal: {
                            buttonLabel: 'privacyverklaring',
                            header:
                              'In deze privacyverklaring leggen wij uit hoe wij omgaan met jouw persoonsgegeven: welke we verzamelen en waarvoor we ze gebruiken. Ook leggen we uit hoe je jouw gegevens kunt inzien, wijzigen of verwijderen. We vinden het belangrijk dat jij hier goed van op de hoogte bent en raden je aan deze verklaring zorgvuldig te lezen.',
                            items: privacyStatementItems,
                            title: 'Privacy beleid',
                          },
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
        content: [
          {
            type: 'FiltersSortingWrapper',
            props: {
              mb: [4, null, 6],
              title: 'Sorteren op:',
              filtersNameField: 'overview.filters',
            },
          },
          {
            type: 'Banner',
            props: {
              mb: 6,
              breakpoint: 'desktop',
              body: "Stap over via de Consumentenbond. Naast eventuele korting van de provider krijg je van ons €10 cashback. Je vindt de korting per abonnement onder 'Kosten & Contract'.",
              iconName: 'finger',
              variant: 'bannerDiscount',
            },
          },
          {
            type: 'Banner',
            props: {
              mb: 6,
              breakpoint: 'mobile',
              body: "Tijdelijk 10 euro cashback op alle pakketten. De cashback vind je bij 'Kosten & Contract'.",
              iconName: 'piggyBank',
              variant: 'bannerDiscountMobile',
            },
          },
          {
            type: 'ProductsNewWrapper',
            props: {
              nextIndex: 3,
              filters: 'overview.filters',
              defaultFilters: 'overview.defaultFilters',
              customProductContent: 'ProductCardTriplePlay',
              disableLoadingSkeleton: false,
              showSubscriptionName: true,
              multiCompare: true,
              promotionBarModals: {
                items: [
                  {
                    dependsOn: {
                      fieldName: '*.isMember',
                      fieldValue: 0,
                    },
                    modal: {
                      title: 'Alle voordelen van het Basislidmaatschap',
                      buttonLabel: '3 maanden gratis Basislidmaatschap',
                      buttonIcon: 'gift',
                      buttonIconProps: { size: 16, fill: 'alert' },
                      items: basicMembershipBenefitsItems,
                    },
                  },
                ],
              },
              getTabs,
              hideProductUsps: true,
            },
          },
          {
            type: 'Div',
            props: {
              height: [20, 35, 50],
              width: '100%',
            },
          },
        ],
        withTopThree: true,
        promoSection: [
          {
            type: 'TopThreeSectionWrapper',
            props: {
              mb: 6,
              label: 'Beste deals',
            },
            _children: [
              {
                type: 'TopThreeProductsWrapper',
                props: {
                  filters: 'overview.filters',
                  customTopThreeCard: 'ProductCardTriplePlayTopThree',
                  defaultFilters: 'overview.defaultFilters',
                  multiCompare: true,
                  nextIndex: 3,
                  swiperThresholdValues: [0, 85, 153],
                  wrapperStyles: {
                    'div': {
                      boxSizing: 'content-box',
                    },
                  },
                },
              },
            ],
          },
        ],
      },
    },
  ],
};

export default overviewLayout;
